import React, { useState, useCallback, useRef, useEffect } from 'react';
import { CloseIcon, PaperclipIcon, PlusIcon, UploadIcon } from './icons';

export interface FileAttachment {
  file: File;
  preview?: string;
}

interface MessageInputProps {
  onSend: (text: string, files?: FileAttachment[]) => void;
  onAttachmentChange?: (attachments: FileAttachment[]) => void;
  disabled?: boolean;
  placeholder?: string;
  isUploading?: boolean;
  uploadDisabled?: boolean;
  uploadAccept?: string | null;
  uploadMultiple?: boolean;
  uploadValidateError?: string | null;
  ariaUploadFile?: string;
  ariaRemoveAttachment?: string;
  // Localized "Uploading files…" announcement while an S3 upload runs
  ariaUploading?: string;
  // Localized name for the "add more files" control in multi-upload mode
  ariaAddMoreFiles?: string;
  // Space-separated element ids describing the upload controls (prompt text)
  describedBy?: string;
}

/**
 * Checks whether a file's MIME type matches an accept pattern string.
 * Supports wildcards like "image/*" and exact types like "application/pdf".
 */
function matchesAccept(file: File, accept: string): boolean {
  const patterns = accept.split(',').map((p) => p.trim().toLowerCase());
  const fileType = file.type.toLowerCase();
  const fileExt = '.' + file.name.split('.').pop()?.toLowerCase();

  return patterns.some((pattern) => {
    if (pattern.startsWith('.')) {
      return fileExt === pattern;
    }
    if (pattern.endsWith('/*')) {
      const category = pattern.slice(0, pattern.indexOf('/'));
      return fileType.startsWith(category + '/');
    }
    return fileType === pattern;
  });
}

/**
 * MessageInput component
 * Shows upload zone when file upload is enabled, and attachment preview when files are selected.
 * The text input and send button are in the bottom bar (ChatWindow).
 */
export const MessageInput: React.FC<MessageInputProps> = ({
  onSend,
  onAttachmentChange,
  disabled = false,
  isUploading = false,
  uploadDisabled = false,
  uploadAccept,
  uploadMultiple = false,
  uploadValidateError,
  ariaUploadFile,
  ariaRemoveAttachment,
  ariaUploading,
  ariaAddMoreFiles,
  describedBy,
}) => {
  const [attachments, setAttachments] = useState<FileAttachment[]>([]);
  const [validationError, setValidationError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (onAttachmentChange) {
      onAttachmentChange(attachments);
    }
  }, [attachments, onAttachmentChange]);

  const processFiles = useCallback((fileList: FileList | File[]) => {
    const files = Array.from(fileList);

    // Filter by accept pattern if specified
    const filtered = uploadAccept
      ? files.filter((f) => matchesAccept(f, uploadAccept))
      : files;

    // Show validation error if some or all files were rejected
    if (uploadAccept && filtered.length < files.length) {
      setValidationError(uploadValidateError || 'File type not allowed.');
    }

    if (filtered.length === 0) return;

    setValidationError(null);

    const newAttachments: FileAttachment[] = [];
    let pending = filtered.length;

    filtered.forEach((file) => {
      const attachment: FileAttachment = { file };

      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (event) => {
          attachment.preview = event.target?.result as string;
          newAttachments.push(attachment);
          pending--;
          if (pending === 0) {
            setAttachments((prev) =>
              uploadMultiple ? [...prev, ...newAttachments] : newAttachments
            );
          }
        };
        reader.readAsDataURL(file);
      } else {
        newAttachments.push(attachment);
        pending--;
        if (pending === 0) {
          setAttachments((prev) =>
            uploadMultiple ? [...prev, ...newAttachments] : newAttachments
          );
        }
      }
    });
  }, [uploadAccept, uploadMultiple, uploadValidateError]);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    processFiles(files);
  }, [processFiles]);

  const handleRemoveAttachment = useCallback((index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  const handleUploadClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleUploadKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleUploadClick();
      }
    },
    [handleUploadClick],
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const files = e.dataTransfer.files;
    if (!files || files.length === 0) return;
    processFiles(files);
  }, [processFiles]);

  if (uploadDisabled) {
    return null;
  }

  const errorId = 'omq-chatbot-upload-error-msg';
  const acceptId = 'omq-chatbot-upload-accept';
  const zoneDescribedBy =
    [describedBy, uploadAccept ? acceptId : null, validationError ? errorId : null]
      .filter(Boolean)
      .join(' ') || undefined;

  return (
    <>
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileSelect}
        accept={uploadAccept || undefined}
        multiple={uploadMultiple}
        style={{ display: 'none' }}
        aria-hidden="true"
        tabIndex={-1}
      />
      <span className="omq-sr-only" role="status" aria-live="polite">
        {isUploading ? ariaUploading || 'Uploading files…' : ''}
      </span>
      {uploadAccept && (
        <span id={acceptId} className="omq-sr-only">
          {uploadAccept}
        </span>
      )}
      {attachments.length > 0 ? (
        <div className="omq-chatbot-attachments-preview">
          {attachments.map((attachment, index) => (
            <div
              key={index}
              className={`omq-chatbot-attachment-preview${isUploading ? ' omq-chatbot-attachment-preview-uploading' : ''}`}>
              {attachment.preview ? (
                <img src={attachment.preview} alt={attachment.file.name} className="omq-chatbot-attachment-preview-image" />
              ) : (
                <div className="omq-chatbot-attachment-preview-file">
                  <span className="omq-chatbot-attachment-icon" aria-hidden="true">
                    <PaperclipIcon />
                  </span>
                  <span className="omq-chatbot-attachment-name">{attachment.file.name}</span>
                </div>
              )}
              {isUploading && (
                <div className="omq-chatbot-attachment-spinner" aria-hidden="true" />
              )}
              {!isUploading && (
                <div className="omq-chatbot-attachment-actions">
                  <button
                    className="omq-chatbot-attachment-remove"
                    onClick={() => handleRemoveAttachment(index)}
                    type="button"
                    aria-label={ariaRemoveAttachment || 'Remove attachment'}
                  >
                    <CloseIcon />
                  </button>
                </div>
              )}
            </div>
          ))}
          {uploadMultiple && (
            <div
              className="omq-chatbot-upload-add-more"
              onClick={handleUploadClick}
              role="button"
              tabIndex={0}
              aria-label={ariaAddMoreFiles || 'Add more files'}
              aria-describedby={zoneDescribedBy}
              onKeyDown={handleUploadKeyDown}
            >
              <PlusIcon />
            </div>
          )}
        </div>
      ) : (
        <div
          className="omq-chatbot-upload-zone"
          onClick={handleUploadClick}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          role="button"
          tabIndex={0}
          aria-label={ariaUploadFile || 'Upload file'}
          aria-describedby={zoneDescribedBy}
          onKeyDown={handleUploadKeyDown}
        >
          <UploadIcon />
        </div>
      )}
      {validationError && (
        <div id={errorId} className="omq-chatbot-upload-error" role="alert">{validationError}</div>
      )}
    </>
  );
};
